import { ActivityWorkflowProvider } from "@hydrologiq/shared/contexts/ActivityWorkflowContextV2"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import MultiScreenDialog from "../navigation/MultiScreenDialog"
import ActivityEditManage from "./editing/ActivityEditManage"
import { ActivityManageScreen } from "./editing/screens"
import ActivityEditProgress from "./editing/ActivityEditProgress"
import ActivityEditAttribute from "./editing/ActivityEditAttribute"
import ActivityEditDetails from "./editing/ActivityEditDetails"
import ActivityDelete from "./editing/ActivityDelete"
import ActivityCreateBatch from "./editing/ActivityCreateBatch"
import ActivityCreateHome from "./creating/ActivityCreateHome"
import ActivityCreateConnect from "./creating/ActivityCreateConnect"
import ActivityCreateAttributes from "./creating/ActivityCreateAttributes"
import { ActivityCreateScreen } from "./creating/screens"
import { ACTIVITY_LINK_ENABLED } from "./ActivityLinkSelector"

interface ActivityManageProps {
  // Mode
  mode?: "create" | "edit"

  // Edit mode props
  activityId?: string
  assetId?: string
  readonly?: boolean
  allowDelete?: boolean

  // Create mode props
  order?: gqlTypes.Order
  asset?: gqlTypes.Asset
  activities?: gqlTypes.Activity[]
  activityKinds?: gqlTypes.ActivityKind[]
  allowAnyAsset?: boolean
  defaultStartDate?: string
  defaultEndDate?: string
  defaultQuantity?: number
  defaultSite?: string
  availableAssets?: gqlTypes.Asset[]
  onActivityCreated?: () => void

  // Common props
  isOpen?: boolean
  onClose?: () => void
  trigger?: React.ReactNode
}

const ActivityManage = (props: ActivityManageProps) => {
  const {
    mode = "edit",
    activityId: defaultActivityId,
    assetId,
    isOpen: controlledIsOpen,
    onClose,
    readonly = false,
    allowDelete,
    order,
    asset,
    activities = [],
    activityKinds,
    allowAnyAsset,
    defaultStartDate,
    defaultEndDate,
    defaultQuantity,
    defaultSite,
    availableAssets,
    onActivityCreated,
    trigger,
  } = props

  const [activityId, setActivityId] = useState<string | undefined>(defaultActivityId)

  // Internal state for uncontrolled mode (when isOpen prop is not provided)
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledIsOpen !== undefined
  const isOpen = isControlled ? controlledIsOpen : internalOpen

  useEffect(() => {
    setActivityId(defaultActivityId)
  }, [defaultActivityId])

  // Determine home screen based on mode
  const HOME = mode === "create" ? ActivityCreateScreen.CreateHome : ActivityManageScreen.ActivityEdit

  // Use union type for screen IDs to support both create and edit screens
  type ScreenId = ActivityManageScreen | ActivityCreateScreen

  const screenHistory = useRef<ScreenId[]>([HOME])
  const [currentScreen, setCurrentScreen] = useState<ScreenId>(HOME)
  const [isCreateHomeValid, setIsCreateHomeValid] = useState(true)
  const [selectedActivityKind, setSelectedActivityKind] = useState<gqlTypes.ActivityKind | undefined>(
    activityKinds?.[0]
  )

  useEffect(() => {
    setCurrentScreen(HOME)
    screenHistory.current = [HOME]
  }, [mode])

  const onScreenChange = (screen: ScreenId) => {
    // Simply push to history - with pop-based back navigation, history stays correct:
    // - Forward navigation: push new screen
    // - Back navigation: pop current screen
    // This naturally handles "went back then took different path" scenario
    screenHistory.current.push(screen)
    setCurrentScreen(screen)
  }

  const handleBack = useCallback(() => {
    // Simply pop current screen and go to previous
    if (screenHistory.current.length > 1) {
      screenHistory.current.pop() // Remove current screen
      const previousScreen = screenHistory.current[screenHistory.current.length - 1]
      setCurrentScreen(previousScreen)
    } else {
      // Already at home or history is empty
      setCurrentScreen(HOME)
    }
  }, [HOME])

  const handleChange = () => {
    onScreenChange(ActivityManageScreen.ActivityEdit)
  }

  const handleClose = useCallback(() => {
    // Reset to home screen on close so next open starts fresh
    setCurrentScreen(HOME)
    screenHistory.current = [HOME]
    
    if (!isControlled) {
      setInternalOpen(false)
    }
    onClose?.()
  }, [HOME, isControlled, onClose])

  const handleActivityCreated = () => {
    if (onActivityCreated) {
      onActivityCreated()
    }
    handleClose()
  }

  const screens = useMemo(() => {
    const editScreens = [
      {
        id: ActivityManageScreen.ActivityEdit,
        title: "Edit activity",
        component: (
          <ActivityEditManage
            activityId={activityId}
            assetId={assetId}
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
            readonly={readonly}
            allowDelete={allowDelete}
          />
        ),
        canGoBack: false,
      },
      {
        id: ActivityManageScreen.ProgressStatus,
        title: "Progress status",
        component: (
          <ActivityEditProgress
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
          />
        ),
        canGoBack: true,
      },
      {
        id: ActivityManageScreen.EditAttribute,
        title: "Edit attribute",
        component: (
          <ActivityEditAttribute
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
          />
        ),
        canGoBack: true,
      },
      {
        id: ActivityManageScreen.EditDetails,
        title: "Edit details",
        component: (
          <ActivityEditDetails
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
          />
        ),
        canGoBack: true,
      },
      {
        id: ActivityManageScreen.ConfirmDelete,
        title: "Delete activity",
        component: (
          <ActivityDelete
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
          />
        ),
        canGoBack: true,
      },
      {
        id: ActivityManageScreen.CreateBatch,
        title: "Create batch",
        component: (
          <ActivityCreateBatch
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
          />
        ),
        canGoBack: true,
        parents: [ActivityManageScreen.EditAttribute, ActivityManageScreen.ProgressStatus],
      },
    ]

    // Determine if we should show the Connect screen (only for Movement activities with linking enabled)
    const shouldShowConnectScreen =
      selectedActivityKind === gqlTypes.ActivityKind.Movement && ACTIVITY_LINK_ENABLED

    // Consistent title across all create screens
    const createTitle = order ? `Order: ${order.name}` : asset ? `Asset: ${asset.name}` : "Create Activity"

    const createScreens = [
      {
        id: ActivityCreateScreen.CreateHome,
        title: createTitle,
        component: (
          <ActivityCreateHome
            activityKinds={activityKinds}
            order={order}
            asset={asset}
            defaultStartDate={defaultStartDate}
            defaultEndDate={defaultEndDate}
            defaultQuantity={defaultQuantity}
            defaultSite={defaultSite}
            availableAssets={availableAssets}
            skipOrderAutoPopulation={!!availableAssets?.length}
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
            onValidationChange={setIsCreateHomeValid}
            onActivityKindChange={setSelectedActivityKind}
          />
        ),
        canGoBack: false,
        canContinue: true,
        onContinue: () =>
          onScreenChange(
            shouldShowConnectScreen ? ActivityCreateScreen.CreateConnect : ActivityCreateScreen.CreateAttributes
          ),
        continueDisabled: !isCreateHomeValid,
      },
      {
        id: ActivityCreateScreen.CreateConnect,
        title: createTitle,
        component: (
          <ActivityCreateConnect
            asset={asset}
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
          />
        ),
        canGoBack: true,
        canContinue: true,
        onContinue: () => onScreenChange(ActivityCreateScreen.CreateAttributes),
        parents: [ActivityCreateScreen.CreateHome],
      },
      {
        id: ActivityCreateScreen.CreateAttributes,
        title: createTitle,
        component: (
          <ActivityCreateAttributes
            order={order}
            asset={asset}
            activities={activities}
            allowAnyAsset={allowAnyAsset}
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
            onActivityCreated={handleActivityCreated}
          />
        ),
        canGoBack: true,
        parents: [ActivityCreateScreen.CreateHome, ActivityCreateScreen.CreateConnect],
      },
      {
        id: ActivityCreateScreen.CreateBatch,
        title: "Create batch",
        component: (
          <ActivityCreateBatch
            mode="create"
            onScreenChange={onScreenChange as any}
            onBack={handleBack}
            onChange={handleChange}
            onClose={handleClose}
          />
        ),
        canGoBack: true,
        parents: [ActivityCreateScreen.CreateAttributes],
      },
    ]

    // Return appropriate screens based on mode
    return mode === "create" ? createScreens : editScreens
  }, [mode, activityId, assetId, order, asset, activities, activityKinds, allowAnyAsset, readonly, allowDelete, isCreateHomeValid, selectedActivityKind])

  const handleSetOpen = useCallback((open: boolean) => {
    if (!isControlled) {
      setInternalOpen(open)
    }
    // Don't call onClose here - handleClose already does that
    // This prevents double-close behavior
  }, [isControlled])

  return (
    <MultiScreenDialog
      screens={screens}
      currentScreenId={currentScreen as string}
      onClose={handleClose}
      open={isOpen}
      setOpen={handleSetOpen}
      trigger={trigger}
      Provider={ActivityWorkflowProvider}
      onBack={handleBack}
    />
  )
}

const ActivityManageWrapped = (props: ActivityManageProps) => {
  // Key only includes stable identity props - assetId removed as it can be unstable during loading
  return <ActivityManage key={`${props.mode}-${props.activityId}`} {...props} />
}

export default ActivityManageWrapped
